import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaLock, FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

const UserProfile = () => {
  const { t } = useTranslation();
  const { currentUser, updateUserProfile, updatePassword, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    email: '',
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [profileStatus, setProfileStatus] = useState({
    loading: false,
    success: false,
    error: null,
  });
  
  const [passwordStatus, setPasswordStatus] = useState({
    loading: false,
    success: false,
    error: null,
  });

  // Load current user data
  useEffect(() => {
    if (currentUser) {
      setProfileForm({
        fullName: currentUser.fullName || '',
        email: currentUser.email || '',
      });
    }
  }, [currentUser]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileStatus({ loading: true, success: false, error: null });
    
    try {
      await updateUserProfile(profileForm);
      setProfileStatus({ loading: false, success: true, error: null });
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setProfileStatus(prev => ({ ...prev, success: false }));
      }, 3000);
    } catch (error) {
      setProfileStatus({
        loading: false,
        success: false,
        error: error.message || t('auth.profileUpdateError')
      });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({
        loading: false,
        success: false,
        error: t('auth.passwordsDoNotMatch')
      });
      return;
    }
    
    setPasswordStatus({ loading: true, success: false, error: null });
    
    try {
      await updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordStatus({ loading: false, success: true, error: null });
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setPasswordStatus(prev => ({ ...prev, success: false }));
      }, 3000);
    } catch (error) {
      setPasswordStatus({
        loading: false,
        success: false,
        error: error.message || t('auth.passwordUpdateError')
      });
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    
    <div className="max-w-4xl mx-auto py-8 px-4">
          {currentUser && currentUser.isVerified === false && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
            >
              <div className="flex items-start">
                <FaExclamationTriangle className="text-yellow-500 mt-1 mr-3 w-12" />
                <div>
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-300">{t('auth.verifyEmailTitle')}</h3>
                  <p className="text-yellow-700 dark:text-yellow-400 mt-1">{t('auth.verifyEmailDescription')}</p>
                  <button
                    onClick={async () => {
                      try {
                        const userEmail = currentUser.email;
                        if (!userEmail) {
                          alert(t('auth.noUserEmail'));
                          return;
                        }
                        await resendVerificationEmail(userEmail);
                        alert(t('auth.verificationEmailSent'));
                      } catch (error) {
                        alert(error.message || t('auth.verificationEmailError'));
                      }
                    }}
                    className="mt-2 text-sm font-medium text-yellow-800 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-200 underline"
                  >
                    {t('auth.resendVerificationEmail')}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        
        <h1 className="text-3xl font-bold mb-8 text-gradient-primary">{t('auth.profileTitle')}</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Profile Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
          >
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaUser className="mr-2 text-primary" />
              {t('auth.personalInfo')}
            </h2>
            
            <form onSubmit={handleProfileSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.fullName')}
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    name="fullName"
                    value={profileForm.fullName}
                    onChange={handleProfileChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
                    placeholder={t('auth.fullNamePlaceholder')}
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={profileForm.email}
                    onChange={handleProfileChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
                    placeholder={t('auth.emailPlaceholder')}
                  />
                </div>
              </div>
              
              {profileStatus.error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md flex items-center">
                  <FaTimes className="mr-2" />
                  {profileStatus.error}
                </div>
              )}
              
              {profileStatus.success && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-md flex items-center">
                  <FaCheck className="mr-2" />
                  {t('auth.profileUpdateSuccess')}
                </div>
              )}
              
              <button
                type="submit"
                disabled={profileStatus.loading}
                className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                {profileStatus.loading ? t('common.saving') : t('auth.saveProfile')}
              </button>
            </form>
          </motion.div>
          
          {/* Password Change - Only show for local auth users */}
          {(!currentUser.authProvider || currentUser.authProvider === 'local') ? (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaLock className="mr-2 text-primary" />
                {t('auth.changePassword')}
              </h2>
              
              <form onSubmit={handlePasswordSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('auth.currentPassword')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <FaLock />
                    </span>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
                      placeholder={t('auth.currentPasswordPlaceholder')}
                      required
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('auth.newPassword')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <FaLock />
                    </span>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
                      placeholder={t('auth.newPasswordPlaceholder')}
                      required
                      minLength="8"
                    />
                  </div>
                  <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                    {t('auth.passwordRequirements')}
                  </p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('auth.confirmPassword')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      <FaLock />
                    </span>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                      required
                    />
                  </div>
                </div>
                
                {passwordStatus.error && (
                  <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md flex items-center">
                    <FaTimes className="mr-2" />
                    {passwordStatus.error}
                  </div>
                )}
                
                {passwordStatus.success && (
                  <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-md flex items-center">
                    <FaCheck className="mr-2" />
                    {t('auth.passwordUpdateSuccess')}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={passwordStatus.loading}
                  className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {passwordStatus.loading ? t('common.saving') : t('auth.updatePassword')}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaLock className="mr-2 text-primary" />
                {t('auth.accountSecurity')}
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                {t('auth.socialAuthPasswordMessage', { provider: currentUser.authProvider === 'google' ? 'Google' : currentUser.authProvider })}
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default UserProfile;
